import { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Sliders, Loader2, Save, Download, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminProgramsPage() {
  const { token } = useAuth();
  const auth = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const [products, setProducts] = useState(null);
  const [ladder, setLadder] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(null);

  const load = async () => {
    const [r1, r2] = await Promise.all([
      fetch(`${API}/programs/admin/rates`, { headers: auth }),
      fetch(`${API}/programs/admin/ap-ladder`, { headers: auth }),
    ]);
    if (!r1.ok) { toast.error('Admin only'); return; }
    const d1 = await r1.json(); const d2 = await r2.json();
    setProducts(d1.products); setLadder(d2.ap_ladder);
    const init = {};
    for (const p of d1.products) init[p.code] = {
      domestic_rate: p.domestic_rate ?? '', international_rate: p.international_rate ?? '',
      base_rate: p.base_rate ?? '', hard_cap_rate: p.hard_cap_rate ?? '',
      default_limit: p.default_limit ?? '', flat_price: p.flat_price ?? '',
    };
    setDrafts(init);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async (code) => {
    setSaving(code);
    try {
      const draft = drafts[code];
      const body = {};
      for (const [k, v] of Object.entries(draft)) {
        if (v === '' || v === null) continue;
        const n = Number(v);
        if (!Number.isNaN(n)) body[k] = n;
      }
      const r = await fetch(`${API}/programs/admin/rates/${code}`, {
        method: 'POST', headers: auth, body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Save failed');
      toast.success(`Saved ${code} · ${d.updated_fields?.join(', ')}`);
      load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(null); }
  };

  const rateCardUrl = `${API}/programs/rate-card.html`;

  if (!products) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="max-w-6xl mx-auto space-y-6 animate-fade-in" data-testid="admin-programs-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Sliders className="w-6 h-6 text-indigo-500" /> Admin · Programs & Rates
          </h1>
          <p className="text-slate-500 mt-1">Edit Retail/Partner/Wholesale rates and product limits. Changes apply immediately.</p>
        </div>
        <Button asChild variant="outline" data-testid="rate-card-link">
          <a href={rateCardUrl} target="_blank" rel="noreferrer"><Download className="w-4 h-4 mr-2" /> Print master rate card <ExternalLink className="w-3 h-3 ml-1" /></a>
        </Button>
      </div>

      {products.map(p => (
        <Card key={p.code} data-testid={`admin-card-${p.code}`}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <span>{p.trademark}</span>
              <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600">{p.code}</span>
            </CardTitle>
            <CardDescription>{p.position} · model: <code className="text-xs">{p.model}</code></CardDescription>
          </CardHeader>
          <CardContent className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {'domestic_rate' in p && (
              <div>
                <Label>Domestic rate {p.model === 'cents_per_100' ? '($/$100)' : ''}</Label>
                <Input type="number" step="0.001" value={drafts[p.code]?.domestic_rate ?? ''} onChange={e => setDrafts(d => ({ ...d, [p.code]: { ...d[p.code], domestic_rate: e.target.value } }))} data-testid={`${p.code}-domestic`} />
              </div>
            )}
            {'international_rate' in p && (
              <div>
                <Label>International rate</Label>
                <Input type="number" step="0.001" value={drafts[p.code]?.international_rate ?? ''} onChange={e => setDrafts(d => ({ ...d, [p.code]: { ...d[p.code], international_rate: e.target.value } }))} data-testid={`${p.code}-intl`} />
              </div>
            )}
            {'base_rate' in p && (
              <div>
                <Label>Base rate ({(p.base_rate * 100).toFixed(2)}%)</Label>
                <Input type="number" step="0.0001" value={drafts[p.code]?.base_rate ?? ''} onChange={e => setDrafts(d => ({ ...d, [p.code]: { ...d[p.code], base_rate: e.target.value } }))} data-testid={`${p.code}-base`} />
              </div>
            )}
            {'hard_cap_rate' in p && (
              <div>
                <Label>Hard cap</Label>
                <Input type="number" step="0.001" value={drafts[p.code]?.hard_cap_rate ?? ''} onChange={e => setDrafts(d => ({ ...d, [p.code]: { ...d[p.code], hard_cap_rate: e.target.value } }))} />
              </div>
            )}
            {'default_limit' in p && (
              <div>
                <Label>Default limit ($)</Label>
                <Input type="number" step="100" value={drafts[p.code]?.default_limit ?? ''} onChange={e => setDrafts(d => ({ ...d, [p.code]: { ...d[p.code], default_limit: e.target.value } }))} />
              </div>
            )}
            {'flat_price' in p && (
              <div>
                <Label>Flat price ($)</Label>
                <Input type="number" step="0.5" value={drafts[p.code]?.flat_price ?? ''} onChange={e => setDrafts(d => ({ ...d, [p.code]: { ...d[p.code], flat_price: e.target.value } }))} />
              </div>
            )}
            <div className="sm:col-span-2 lg:col-span-4 flex justify-end">
              <Button size="sm" onClick={() => save(p.code)} disabled={saving === p.code} data-testid={`save-${p.code}`}>
                {saving === p.code ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                Save {p.code}
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}

      {ladder && (
        <Card>
          <CardHeader><CardTitle className="text-base">AP Underwriting Ladder</CardTitle><CardDescription>Read-only governance table.</CardDescription></CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead><tr className="text-slate-500 border-b"><th className="text-left py-2">Level</th><th className="text-right">Max exposure</th><th className="text-left pl-4">Auto</th><th className="text-left">Requires</th></tr></thead>
              <tbody>
                {ladder.map(a => (
                  <tr key={a.level} className="border-b last:border-b-0">
                    <td className="py-2 font-mono text-xs">{a.level}</td>
                    <td className="text-right tabular-nums">${a.max_exposure.toLocaleString()}</td>
                    <td className="pl-4">{a.auto ? '✓' : '—'}</td>
                    <td className="text-slate-500 text-xs">{a.requires}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
